import { forwardRef, useLayoutEffect, useMemo, useRef } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { ScrollContainer } from '../ScrollContainer';
import { Text } from '../Text';
import {
	StyledItemsContainer,
	StyledItemsContainerInner,
	StyledSelectIcon,
	StyledSelectItem,
	StyledSelectItemWrapper,
} from './SelectDropdown.styled';

interface ISelectDropdownProps {
	items: string[];
	value: string;
	handleSelect: (items: string) => void;
	visible: boolean;
	dropdownHeight?: string;
}

const SelectDropdown = forwardRef<HTMLDivElement, ISelectDropdownProps>(
	(
		{
			items,
			handleSelect,
			value,
			visible,
			dropdownHeight = '290px',
		}: ISelectDropdownProps,
		ref,
	) => {
		const scrollbar = useMemo(() => items.length > 3, [items]);
		const activeItemRef = useRef<HTMLDivElement>(null);

		useLayoutEffect(() => {
			if (!visible || !scrollbar) {
				return;
			}

			const activeElement = activeItemRef.current;
			if (!activeElement) {
				return;
			}

			let scrollContainer: HTMLElement | null = activeElement.parentElement;
			while (scrollContainer) {
				const style = window.getComputedStyle(scrollContainer);
				if (style.overflowY === 'scroll' || style.overflowY === 'auto') {
					break;
				}
				scrollContainer = scrollContainer.parentElement;
			}

			if (!scrollContainer) {
				return;
			}

			const activeIndex = items.findIndex((item) => item === value);
			if (activeIndex <= 0) {
				scrollContainer.scrollTop = 0;
				return;
			}

			const containerRect = scrollContainer.getBoundingClientRect();
			const elementRect = activeElement.getBoundingClientRect();
			const elementTop =
				elementRect.top - containerRect.top + scrollContainer.scrollTop;
			const targetScrollTop =
				elementTop -
				scrollContainer.clientHeight / 2 +
				activeElement.offsetHeight / 2;

			scrollContainer.scrollTop = targetScrollTop;
		}, [visible, scrollbar, items, value]);

		return (
			<StyledItemsContainer ref={ref} tabIndex={-1} $height={dropdownHeight}>
				<ScrollContainer
					height={parseInt(dropdownHeight)}
					scrollbarHeight={parseInt(dropdownHeight) - 20}
					scrollbarTop={10}
					scrollbarRight={5}
					scrollbarBlackLine
				>
					<StyledItemsContainerInner>
						{items.map((item, index) => (
							<StyledSelectItemWrapper
								key={index}
								ref={item === value ? activeItemRef : undefined}
								onClick={() => {
									handleSelect(item);
									playSound(Sound.ClickButton);
								}}
								$scrollbar={scrollbar}
								$active={item === value}
								className={`${item === value ? 'active' : ''}`}
								onMouseEnter={() => {
									if (visible) {
										playSound(Sound.LinkCursor);
									}
								}}
							>
								<StyledSelectItem>
									{item === value && <StyledSelectIcon />}
									<Text>{item}</Text>
								</StyledSelectItem>
							</StyledSelectItemWrapper>
						))}
					</StyledItemsContainerInner>
				</ScrollContainer>
			</StyledItemsContainer>
		);
	},
);

export { SelectDropdown };
